// @flow
/**
 * Enrollment Error Modal
 * 
 * Displays error messages during the enrollment flow with:
 * - Context-specific error messages based on error code
 * - Retry mechanism for recoverable errors
 * - Start over option for fatal errors
 * - Rollback notification when MFA was cleaned up
 */

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'connected-react-router';

// Components
import { Modal } from '../..';
import Content from '../../Content/Content';
import { ButtonPrimary, Button } from 'texkit/dist/components/Buttons';

// Actions
import { closeModal } from '../../../../reducers/modal/modalActions';
import { 
  retryEnrollmentStep, 
  resetEnrollment,
  cancelEnrollment,
  ENROLLMENT_ERROR_CODES,
} from '../../../../reducers/enrollment/enrollmentActions';

// Styles
import './enrollment-error-modal.scss';

// Types
type Props = {
  modal: Object,
  enrollment: Object,
  closeModal: Function,
  retryEnrollmentStep: Function,
  resetEnrollment: Function,
  cancelEnrollment: Function,
  push: Function,
};

/**
 * Get icon based on error severity
 */
const getErrorIcon = (errorCode: ?string) => {
  switch (errorCode) {
    case ENROLLMENT_ERROR_CODES.NETWORK_ERROR:
      return '🌐';
    case ENROLLMENT_ERROR_CODES.SESSION_EXPIRED:
    case ENROLLMENT_ERROR_CODES.TOKEN_EXPIRED:
      return '⏱️';
    case ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_NOT_SUPPORTED:
      return '🔐';
    case ENROLLMENT_ERROR_CODES.ROLLBACK_FAILED:
      return '⚠️';
    default:
      return '❌';
  }
};

/**
 * Get title based on error type
 */
const getErrorTitle = (errorCode: ?string, wasRolledBack: boolean) => {
  if (wasRolledBack) {
    return 'Registration Rolled Back';
  }
  
  switch (errorCode) {
    case ENROLLMENT_ERROR_CODES.NETWORK_ERROR:
      return 'Connection Lost';
    case ENROLLMENT_ERROR_CODES.SESSION_EXPIRED:
    case ENROLLMENT_ERROR_CODES.TOKEN_EXPIRED:
      return 'Session Expired';
    case ENROLLMENT_ERROR_CODES.MFA_OTP_INVALID:
      return 'Invalid Code';
    case ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_CANCELLED:
      return 'Registration Cancelled';
    case ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_NOT_SUPPORTED:
      return 'Not Supported';
    case ENROLLMENT_ERROR_CODES.PASSWORD_SAVE_FAILED:
      return 'Account Setup Failed';
    case ENROLLMENT_ERROR_CODES.ROLLBACK_FAILED:
      return 'Cleanup Warning';
    default:
      return 'Registration Error';
  }
};

const EnrollmentErrorModal = ({ 
  modal: { data }, 
  enrollment,
  closeModal, 
  retryEnrollmentStep, 
  resetEnrollment,
  cancelEnrollment,
  push,
}: Props) => {
  const errorCode = (data && data.errorCode) || (enrollment && enrollment.errorCode);
  const errorMessage = (data && data.error) || (enrollment && enrollment.error) || 'An error occurred during enrollment.';
  const canRetry = !(data && data.canRetry === false);
  const showStartOver = (data && data.showStartOver) || !canRetry;
  const wasRolledBack = (data && data.wasRolledBack) || (enrollment && enrollment.wasRolledBack);
  const step = (data && data.step) || (enrollment && enrollment.lastFailedStep);

  const handleRetry = () => {
    closeModal('EnrollmentErrorModal');
    if (step) {
      retryEnrollmentStep(step);
    } else {
      push('/mfa-registration');
    }
  };

  const handleStartOver = () => {
    closeModal('EnrollmentErrorModal');
    resetEnrollment();
  };

  const handleCancel = () => {
    closeModal('EnrollmentErrorModal');
    cancelEnrollment();
  };

  const handleContactSupport = () => {
    window.open('https://dir.texas.gov/contact', '_blank');
  };

  const handleChooseDifferentMethod = () => {
    closeModal('EnrollmentErrorModal');
    push('/mfa-registration');
  };

  return (
    <Modal 
      name="EnrollmentErrorModal" 
      className="modal-sm enrollment-error-modal"
      disableCloseButton={true}
    >
      <div className="enrollment-error-icon">
        {getErrorIcon(errorCode)}
      </div>
      
      <h2 className="h1 modal-title">
        {getErrorTitle(errorCode, wasRolledBack)}
      </h2>
      
      <div className="modal-content">
        <p className="error-message">{errorMessage}</p>
        
        {wasRolledBack && (
          <div className="rollback-notice">
            <p>
              <strong>Note:</strong> Your MFA registration has been automatically 
              removed to prevent incomplete account setup. You can safely try again.
            </p>
          </div>
        )}

        {errorCode === ENROLLMENT_ERROR_CODES.ROLLBACK_FAILED && (
          <div className="warning-notice">
            <p>
              There may be an orphaned MFA device on your account. 
              Please contact support if you experience issues.
            </p>
          </div>
        )}

        <div className="button-group">
          {canRetry && (
            <ButtonPrimary onClick={handleRetry}>
              Try Again
            </ButtonPrimary>
          )}
          
          {errorCode === ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_NOT_SUPPORTED && (
            <ButtonPrimary onClick={handleChooseDifferentMethod}>
              Choose Different Method
            </ButtonPrimary>
          )}
          
          {showStartOver && (
            <Button onClick={handleStartOver}>
              Start Over
            </Button>
          )}
          
          <Button onClick={handleCancel}>
            Cancel Enrollment
          </Button>
        </div>

        <button
          type="button"
          onClick={handleContactSupport}
          className="support-link"
        >
          Contact Support
        </button>
        
        {enrollment && enrollment.retryCount > 0 && (
          <p className="retry-count">
            Attempt {enrollment.retryCount} of 3
          </p>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = ({ modal, enrollment }): Object => ({ modal, enrollment });
const mapDispatchToProps = (dispatch: Object) =>
  bindActionCreators({ 
    closeModal, 
    retryEnrollmentStep, 
    resetEnrollment, 
    cancelEnrollment,
    push,
  }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(EnrollmentErrorModal);
